import { type Type, type Provider, type InputSignal, type OutputEmitterRef, type WritableSignal } from "@angular/core";
import { IAction } from "@shared/interfaces";
import { SafeHtml } from "@angular/platform-browser";
import { WkDataTableSimpleColumn, WkDataTableDynamicColumn } from "./columns";
import { WkDataTableRow } from "./rows";
import { WkDataTableRegistrationResolver } from "./data-table.resolver";
import { TranslateObject } from "@app/views-interfaces";

/**
 * Field and Value types from a generic type TItem
 */

export type WkDataTableId = string;
export type WkDataTableFieldName = string;
export type WkDataTableColumnValue<TItem, TValue extends WkDataTableCellValue> = (item: TItem) => TValue;
export type WkDataTableColumnsByField = Map<WkDataTableFieldName, WkDataTableSimpleColumn<any, any>>;

/**
 * Toolbar items
 */

export interface WkDataTableToolbarItem {
    icon?: string;
    title: string;
    event: () => void;
    disabled?: () => boolean;
}

export type WkDataTableToolbarItems = WkDataTableToolbarItem[];

/**
 * Data and columns types
 */

export enum WkDataTableFieldTypeEnum {
    String = "STRING",
    Number = "NUMBER",
    Boolean = "BOOLEAN",
    Datetime = "DATETIME",
    Timestamp = "TIMESTAMP",
}

export enum WkDataTableInputTypeEnum {
    Text = "TEXT",
    Toggle = "TOGGLE",
}

export type WkDataTableCellValue = number | string | boolean | undefined | null | SafeHtml;

/**
 * Columns types (definition, internal and saved)
 */

export interface WkDataTableColumnBaseDefinition {
    fieldname: WkDataTableFieldName;
}

export interface WkDataTableColumnDefinition extends WkDataTableColumnBaseDefinition {
    title: string;
    type: WkDataTableFieldTypeEnum;
    dynamicFieldName?: string;
    width?: number;
    input?: WkDataTableInputTypeEnum;
    sortable?: boolean;
    filterable?: boolean;
    searchable?: boolean;
    exactMatch?: boolean;
    visible?: boolean;
    locked?: boolean;
    reorderable?: boolean;
    export?: boolean;
    resource?: string;
    position?: number;
    sorted?: boolean;
    sortDirection?: WkDataTableSortDirectionEnum;
    sortFallback?: WkDataTableBaseSortItem[];
}

export type WkDataTableColumns = WkDataTableSimpleColumn<any, any>[];

export interface WkDataTableStateColumn {
    position: number;
    visible: boolean;
    sorted: boolean;
    sortDirection: WkDataTableSortDirectionEnum;
    sortOrder: number;
}

export type WkDataTableStateColumns = Record<WkDataTableFieldName, Partial<WkDataTableStateColumn>>;

export interface WkDataTableStoredColumnState {
    version: 1;
    columns: WkDataTableStateColumns;
}

/**
 * Actions types
 */

export type WkDataTableAction<TItem> = Omit<IAction, "event" | "icon" | "title"> & {
    icon: string | ((item?: WkDataTableInternalDataItem<TItem>) => string);
    title: string | TranslateObject | ((item?: WkDataTableInternalDataItem<TItem>) => string);
    event: (item: WkDataTableInternalDataItem<TItem> | undefined) => void;
    selectionEvent?: (selectedItems: WkDataTableInternalData<TItem>) => void;
    visibleOnSelectedItems?: boolean;
};

export type WkDataTableActions<TItem> = WkDataTableAction<TItem>[];

export type WkDataTableActionsFunction<TItem> = (item?: WkDataTableInternalDataItem<TItem> | undefined) => WkDataTableActions<TItem>;

/**
 * Sorting types
 */

export enum WkDataTableSortDirectionEnum {
    Asc = "ASC",
    Desc = "DESC",
}

export interface WkDataTableBaseSortItem {
    fieldname: WkDataTableFieldName;
    direction: WkDataTableSortDirectionEnum;
    type?: WkDataTableFieldTypeEnum;
}

export interface WkDataTableSortItem extends WkDataTableBaseSortItem {
    order: number;
}

export type WkDataTableSortItems = Map<WkDataTableFieldName, WkDataTableSortItem> | undefined;

export interface WkDataTableSortingOptions {
    multiple?: boolean;
    save?: boolean;
    items?: WkDataTableSortItems;
}

export type WkDataTableSorting = WkDataTableSortingOptions | undefined;

export const DEFAULT_SORTING = {
    multiple: false,
    save: false,
    items: undefined,
};

/**
 * Data types (definition and internal)
 */

export type WkDatableData<TItem> = TItem[];

export type WkDatatableRowFields = Record<WkDataTableFieldName, WkDataTableCellValue>;

export interface WkDataTableInternalDataItem<TItem> {
    get value(): TItem;
    fields: WkDatatableRowFields;
    $selected: boolean;
    $edited: boolean;
    $searchCache?: WkDataTableItemSearchCache;
    updateField?: (field: WkDataTableFieldName, value: WkDataTableCellValue) => void;
    markEdited?: () => void;
}

export type WkDataTableInternalData<TItem> = WkDataTableInternalDataItem<TItem>[];

/**
 * Search types
 */

type WkDataTableItemSearchCache = Array<{ value: string; exactMatch: boolean }>;

export interface WkDataTableSearchValueItem {
    value: string;
    exactMatch: boolean;
}

export type WkDataTableSearchValueItems = WkDataTableSearchValueItem[];

export interface WkDataTableSearchIndexItem<TItem> {
    item: WkDataTableInternalDataItem<TItem>;
    search: WkDataTableSearchValueItems;
}

export type WkDataTableSearchIndexItems<TItem> = WkDataTableSearchIndexItem<TItem>[];

/**
 * Columns Definition
 */

export type ColumnType = Type<WkDataTableSimpleColumn<any, any>> | Type<WkDataTableDynamicColumn<any, any, any>>;

export interface ProvideWkDataTableOptions {
    id: WkDataTableId;
    resolver: Type<WkDataTableRegistrationResolver<WkDataTableColumnRegistration, WkDataTableRowRegistration>>;
    columnsProvider: Provider[];
    rowProvider: Provider[];
}

export interface WkDataTableColumnRegistration {
    service: ColumnType;
}

/**
 * Rows Definition
 */

export interface WkDataTableRowCalcFields {
    [key: string]: any;
}

export type RowType = Type<WkDataTableRow<any, WkDataTableRowCalcFields>>;

export interface WkDataTableRowRegistration {
    service: RowType;
}

/**
 * Context
 */

export interface WkDataTableContext<TItem> {
    enableRowClick: InputSignal<boolean>;
    onRowClick: OutputEmitterRef<WkDataTableInternalDataItem<TItem>>;
    selectable: InputSignal<boolean>;
    data: WritableSignal<WkDataTableInternalData<TItem>>;
    sorting: InputSignal<WkDataTableSorting>;
    primaryField: InputSignal<keyof TItem & string>;
    actions: InputSignal<WkDataTableActions<TItem>>;
    reorderableColumns: InputSignal<boolean>;
}

export type DynamicColumns<
    TItem,
    TValue extends WkDataTableCellValue,
    TSortValue extends WkDataTableCellValue = TValue,
    TColumnDefinition extends WkDataTableColumnDefinition = WkDataTableColumnDefinition,
> = Map<WkDataTableFieldName, WkDataTableSimpleColumn<TItem, TValue, TSortValue, TColumnDefinition>>;
